import { useEffect, useRef, useState } from "react";
import { useVariation } from "@hackler/react-sdk";
import { customEvent, SafeEventProperties } from "goi_common";
import { useIsDevEnvironment } from "./useIsDevEnvironment";
import {
  HACKLE_DEVTOOLS_STORAGE_KEY,
  HACKLE_VARIATIONS,
  registerExperiment,
} from "@/components/GoiHackleDevtools";

type VariationMap = Record<number, string>;

export const useCustomVariation = (
  experimentKey: number,
  defaultVariation?: string,
  contextExtra?: SafeEventProperties
): string => {
  const variation = useVariation(experimentKey, defaultVariation || "Z");

  const isDevEnvironment = useIsDevEnvironment();
  const [clientVariation, setClientVariation] = useState(
    defaultVariation || "Z"
  );

  useEffect(() => {
    if (typeof window === "undefined") return;

    if (isDevEnvironment === true) {
      registerExperiment(experimentKey);

      const stored = window.localStorage.getItem(HACKLE_DEVTOOLS_STORAGE_KEY);
      if (stored) {
        try {
          const variations: VariationMap = JSON.parse(stored);
          const testVariation = variations[experimentKey];

          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          if (HACKLE_VARIATIONS.includes(testVariation as any)) {
            setClientVariation(testVariation);
            return;
          }
        } catch {
          // ignore
        }
      }

      setClientVariation(variation);
    }
  }, [experimentKey, isDevEnvironment, variation]);

  const resolvedVariation =
    isDevEnvironment === true ? clientVariation : variation;

  const exposedKeyRef = useRef<string>("");

  useEffect(() => {
    if (resolvedVariation === "Z") return;

    const experimentVariation = `${experimentKey}-${resolvedVariation}`;
    if (exposedKeyRef.current === experimentVariation) return;
    exposedKeyRef.current = experimentVariation;

    customEvent({
      action: "experiment_exposure",
      properties: {
        experiment_variation: experimentVariation,
        ...(contextExtra || {}),
      },
    });
  }, [resolvedVariation, experimentKey]); // eslint-disable-line react-hooks/exhaustive-deps

  return resolvedVariation;
};
